import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  Check,
  Clock,
  CornerDownRight,
  ExternalLink,
  Plus,
  RotateCcw,
  Search,
  X,
} from 'lucide-react';
import type {
  ProjectDoc,
  SuggestionCandidate,
  SuggestionStatus,
  WbsItemDoc,
} from '@mct/shared';
import { formatBacklogProjectLabel } from '@mct/shared';
import { useAuthState } from '../hooks/useAuth';
import { useProject, useAssignees } from '../hooks/useFirestoreData';
import { useWbsItems } from '../hooks/useWbs';
import { useBacklogSearch } from '../hooks/useBacklogSearch';
import { useBacklogIssueMeta } from '../hooks/useBacklogIssueMeta';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Spinner } from '../components/ui/Spinner';
import { Dialog } from '../components/ui/Dialog';
import { Input, Label } from '../components/ui/Input';
import { cn } from '../lib/utils';
import { CreateAndLinkDialog } from '../components/SuggestionInbox/CreateAndLinkDialog';
import { appendWbsItem } from '../lib/wbs';
import { getDb } from '../lib/firebase';
import {
  FILTER_LABEL,
  SNOOZE_OPTIONS,
  UPDATED_SINCE_LABEL,
  UPDATED_SINCE_PRESETS,
  classifyCallableError,
  decodeInboxFromParams,
  effectiveActionedState,
  encodeInboxToParams,
  filterSearchResults,
  suggestionActions,
  updatedSincePresetToIso,
  type CallableErrorKind,
  type InboxUrlState,
  type ReactOnSuggestionSnapshot,
  type SuggestionFilter,
} from '../lib/suggestion';
import type {
  BacklogSearchResultItem,
  SearchBacklogIssuesFilter,
} from '../lib/backlogSearch';

const FILTER_ORDER: readonly SuggestionFilter[] = [
  'pending',
  'snoozed',
  'all',
  'terminal',
];

const KEYWORD_DEBOUNCE_MS = 400;

export function SuggestionInboxPage() {
  const { projectId } = useParams<{ projectId: string }>();
  if (!projectId) return null;
  return <InboxContent projectId={projectId} />;
}

function InboxContent({ projectId }: { projectId: string }) {
  const { user } = useAuthState();
  const { data: project } = useProject(projectId);
  const { data: wbsItems } = useWbsItems(projectId);

  const [searchParams, setSearchParams] = useSearchParams();
  const urlState = useMemo(
    () => decodeInboxFromParams(searchParams),
    [searchParams],
  );

  // Keep an unsynced "draft keyword" in component state so typing is
  // smooth, then sync into URL state (which drives the network) on a
  // debounce. This prevents a fetch on every keystroke.
  const [draftKeyword, setDraftKeyword] = useState(urlState.keyword);
  useEffect(() => {
    setDraftKeyword(urlState.keyword);
  }, [urlState.keyword]);
  useEffect(() => {
    if (draftKeyword === urlState.keyword) return;
    const handle = window.setTimeout(() => {
      setSearchParams(
        encodeInboxToParams({ ...urlState, keyword: draftKeyword }, searchParams),
        { replace: true },
      );
    }, KEYWORD_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [draftKeyword, urlState, searchParams, setSearchParams]);

  // Build the server-side filter from URL state. We resolve the
  // updated-since preset to a concrete ISO at render time so the
  // network filter is stable on a calendar-day cadence.
  const serverFilter: SearchBacklogIssuesFilter = useMemo(() => {
    const iso = updatedSincePresetToIso(urlState.updatedSince);
    return {
      statusIds: urlState.statusIds.length > 0 ? urlState.statusIds : undefined,
      assigneeIds: urlState.assigneeIds.length > 0 ? urlState.assigneeIds : undefined,
      categoryIds: urlState.categoryIds.length > 0 ? urlState.categoryIds : undefined,
      milestoneIds: urlState.milestoneIds.length > 0 ? urlState.milestoneIds : undefined,
      priorityIds: urlState.priorityIds.length > 0 ? urlState.priorityIds : undefined,
      backlogProjectKeys:
        urlState.backlogProjectKeys.length > 0
          ? urlState.backlogProjectKeys
          : undefined,
      keyword: urlState.keyword.trim().length > 0 ? urlState.keyword.trim() : undefined,
      updatedSinceIso: iso ?? undefined,
    };
  }, [urlState]);

  // Cascade: the meta callable's dropdown options are narrowed by the
  // very same filter the search uses. Without this the option lists
  // remain "every used id across every project", which defeats the
  // point of selecting a project at all.
  const metaFilter = useMemo(
    () => ({
      statusIds: urlState.statusIds.length > 0 ? urlState.statusIds : undefined,
      assigneeIds: urlState.assigneeIds.length > 0 ? urlState.assigneeIds : undefined,
      categoryIds: urlState.categoryIds.length > 0 ? urlState.categoryIds : undefined,
      milestoneIds: urlState.milestoneIds.length > 0 ? urlState.milestoneIds : undefined,
      priorityIds: urlState.priorityIds.length > 0 ? urlState.priorityIds : undefined,
      backlogProjectKeys:
        urlState.backlogProjectKeys.length > 0
          ? urlState.backlogProjectKeys
          : undefined,
    }),
    [
      urlState.statusIds,
      urlState.assigneeIds,
      urlState.categoryIds,
      urlState.milestoneIds,
      urlState.priorityIds,
      urlState.backlogProjectKeys,
    ],
  );

  const search = useBacklogSearch(projectId, serverFilter);
  const meta = useBacklogIssueMeta(projectId, metaFilter);
  const { data: assignees } = useAssignees(projectId);

  // Aggregate the per-callable backlogProjects lists into one
  // key→name lookup. The persisted snapshot on the project doc seeds
  // the map so chips / filter pickers / row labels render names from
  // the very first paint — without it, the first frame shows bare keys
  // until either callable returns. Live results then layer on top so a
  // Backlog-side rename is reflected as soon as the next probe completes.
  const projectNameByKey = useMemo(() => {
    const m = new Map<string, string>();
    const fromDoc = project?.backlog?.backlogProjectNames ?? {};
    for (const [k, v] of Object.entries(fromDoc)) {
      if (typeof v === 'string' && v.trim().length > 0) m.set(k, v);
    }
    const fromMeta = meta.data?.ok ? meta.data.backlogProjects : [];
    const fromSearch = search.backlogProjects;
    for (const p of fromMeta) m.set(p.key, p.name);
    for (const p of fromSearch) if (!m.has(p.key)) m.set(p.key, p.name);
    return m;
  }, [project, meta.data, search.backlogProjects]);

  const setFilter = (filter: SuggestionFilter) => {
    setSearchParams(
      encodeInboxToParams({ ...urlState, filter }, searchParams),
      { replace: true },
    );
  };
  const clearWbsItemFilter = () => {
    setSearchParams(
      encodeInboxToParams({ ...urlState, wbsItemId: null }, searchParams),
      { replace: true },
    );
  };
  const updateUrlState = (patch: Partial<InboxUrlState>) => {
    setSearchParams(
      encodeInboxToParams({ ...urlState, ...patch }, searchParams),
      { replace: true },
    );
  };

  const filtered = useMemo(() => {
    const base = filterSearchResults(search.data, urlState.filter);
    if (!urlState.wbsItemId) return base;
    return base.filter((s) =>
      s.candidateWbsItemIds.includes(urlState.wbsItemId!),
    );
  }, [search.data, urlState.filter, urlState.wbsItemId]);

  const wbsItemName = useMemo(() => {
    if (!urlState.wbsItemId) return null;
    return wbsItems.find((w) => w.itemId === urlState.wbsItemId)?.name ?? null;
  }, [wbsItems, urlState.wbsItemId]);

  const [error, setError] = useState<{ kind: CallableErrorKind; msg: string } | null>(
    null,
  );
  const [toast, setToast] = useState<string | null>(null);

  return (
    <div
      data-testid="suggestion-inbox-root"
      className="flex h-full flex-col overflow-hidden"
    >
      <div className="flex flex-wrap items-start justify-between gap-2 border-b border-slate-200 bg-white px-4 py-3 sm:items-center sm:gap-3 sm:px-6">
        <div className="min-w-0">
          <h2 className="truncate text-lg font-semibold">リンク候補</h2>
          <p className="hidden text-xs text-slate-500 sm:block">
            Backlog 課題とタスクの紐付け候補を Backlog API から検索します
          </p>
        </div>
        <div
          data-testid="suggestion-inbox-tabs"
          className="-mx-4 flex w-full flex-nowrap gap-1 overflow-x-auto px-4 sm:mx-0 sm:w-auto sm:overflow-visible sm:px-0"
        >
          {FILTER_ORDER.map((f) => (
            <FilterTab
              key={f}
              active={urlState.filter === f}
              onClick={() => setFilter(f)}
            >
              {FILTER_LABEL[f]}
            </FilterTab>
          ))}
        </div>
      </div>

      <FilterBar
        urlState={urlState}
        draftKeyword={draftKeyword}
        onKeywordChange={setDraftKeyword}
        meta={meta.data}
        metaLoading={meta.loading}
        onPatch={updateUrlState}
        projectNameByKey={projectNameByKey}
      />

      <AppliedFiltersRow
        urlState={urlState}
        meta={meta.data}
        projectNameByKey={projectNameByKey}
        onPatch={updateUrlState}
      />

      {urlState.wbsItemId ? (
        <div className="flex items-center gap-2 border-b border-slate-200 bg-blue-50 px-4 py-2 text-xs sm:px-6">
          <span className="text-slate-600">WBS フィルタ:</span>
          <Badge tone="blue">{wbsItemName ?? urlState.wbsItemId}</Badge>
          <button
            className="inline-flex items-center gap-1 text-blue-700 hover:underline"
            onClick={clearWbsItemFilter}
          >
            <X className="h-3 w-3" />
            解除
          </button>
        </div>
      ) : null}

      {search.failure ? (
        <ServerFailureBanner failure={search.failure} />
      ) : null}
      {error ? (
        <ErrorBanner kind={error.kind} message={error.msg} onClose={() => setError(null)} />
      ) : null}
      {toast ? (
        <SuccessBanner message={toast} onClose={() => setToast(null)} />
      ) : null}

      <div className="flex-1 overflow-auto">
        {search.loading && search.data.length === 0 ? (
          <div className="flex h-full items-center justify-center">
            <Spinner />
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState filter={urlState.filter} loading={search.loading} />
        ) : (
          <ul className="space-y-3 px-4 py-4 sm:px-6">
            {filtered.map((s) => (
              <SuggestionCard
                key={s.issueKey}
                project={project}
                item={s}
                actionable={
                  Boolean(user) &&
                  urlState.filter !== 'terminal' &&
                  (effectiveActionedState(s) === 'pending' ||
                    effectiveActionedState(s) === 'snoozed')
                }
                projectNameByKey={projectNameByKey}
                wbsItems={wbsItems}
                assignees={assignees}
                onError={setError}
                onSuccess={(msg) => {
                  setToast(msg);
                  search.refresh();
                }}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function FilterBar({
  urlState,
  draftKeyword,
  onKeywordChange,
  meta,
  metaLoading,
  onPatch,
  projectNameByKey,
}: {
  urlState: InboxUrlState;
  draftKeyword: string;
  onKeywordChange: (v: string) => void;
  meta: ReturnType<typeof useBacklogIssueMeta>['data'];
  metaLoading: boolean;
  onPatch: (patch: Partial<InboxUrlState>) => void;
  projectNameByKey: ReadonlyMap<string, string>;
}) {
  const statuses = meta?.ok ? meta.statuses : [];
  const users = meta?.ok ? meta.users : [];
  const categories = meta?.ok ? meta.categories : [];
  const milestones = meta?.ok ? meta.milestones : [];
  const backlogProjects = meta?.ok ? meta.backlogProjects : [];

  // Per-axis options grouped by backlogProjectKey. Dedupe key is the
  // (backlogProjectKey, id) pair — id alone collides across Backlog
  // projects (their masters are project-scoped, not space-scoped).
  const statusGroups = groupByProjectKey(
    statuses.map((s) => ({ id: s.id, label: s.name, backlogProjectKey: s.backlogProjectKey })),
  );
  const userGroups = groupByProjectKey(
    users.map((u) => ({ id: u.id, label: u.name, backlogProjectKey: u.backlogProjectKey })),
  );
  const categoryGroups = groupByProjectKey(
    categories.map((c) => ({ id: c.id, label: c.name, backlogProjectKey: c.backlogProjectKey })),
  );
  const milestoneGroups = groupByProjectKey(
    milestones.map((m) => ({
      id: m.id,
      label: m.archived ? `${m.name}（アーカイブ）` : m.name,
      backlogProjectKey: m.backlogProjectKey,
    })),
  );

  const totalCount = (gs: readonly { items: readonly unknown[] }[]) =>
    gs.reduce((acc, g) => acc + g.items.length, 0);

  // Backlog プロジェクト axis. The picker is rendered whenever the meta
  // callable returned 2+ keys; with a single Backlog project the picker
  // would be visual noise (nothing to choose between).
  const backlogProjectOptions = backlogProjects.map((p) => ({
    id: p.key,
    label: formatBacklogProjectLabel(p.key, p.name),
  }));

  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50 px-4 py-2 text-xs sm:px-6">
      {backlogProjectOptions.length > 1 || metaLoading ? (
        <StringMultiSelect
          label="Backlog"
          options={backlogProjectOptions}
          selected={urlState.backlogProjectKeys}
          onChange={(ks) => onPatch({ backlogProjectKeys: ks })}
          loading={metaLoading}
        />
      ) : null}

      <div className="relative flex-1 min-w-[12rem] max-w-md">
        <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
        <input
          aria-label="キーワード"
          className="w-full rounded border border-slate-200 bg-white py-1 pl-7 pr-2 text-sm focus:border-slate-400 focus:outline-none"
          placeholder="キーワード"
          value={draftKeyword}
          onChange={(e) => onKeywordChange(e.target.value)}
        />
      </div>

      {totalCount(statusGroups) > 0 || metaLoading ? (
        <GroupedMultiSelect
          label="状態"
          groups={statusGroups}
          selected={urlState.statusIds}
          onChange={(ids) => onPatch({ statusIds: ids })}
          loading={metaLoading}
          projectNameByKey={projectNameByKey}
        />
      ) : null}
      {totalCount(userGroups) > 0 || metaLoading ? (
        <GroupedMultiSelect
          label="担当者"
          groups={userGroups}
          selected={urlState.assigneeIds}
          onChange={(ids) => onPatch({ assigneeIds: ids })}
          loading={metaLoading}
          projectNameByKey={projectNameByKey}
        />
      ) : null}
      {totalCount(categoryGroups) > 0 ? (
        <GroupedMultiSelect
          label="カテゴリ"
          groups={categoryGroups}
          selected={urlState.categoryIds}
          onChange={(ids) => onPatch({ categoryIds: ids })}
          loading={metaLoading}
          projectNameByKey={projectNameByKey}
        />
      ) : null}
      {totalCount(milestoneGroups) > 0 ? (
        <GroupedMultiSelect
          label="マイルストーン"
          groups={milestoneGroups}
          selected={urlState.milestoneIds}
          onChange={(ids) => onPatch({ milestoneIds: ids })}
          loading={metaLoading}
          projectNameByKey={projectNameByKey}
        />
      ) : null}

      <div className="flex items-center gap-1">
        <span className="text-slate-500">更新</span>
        <select
          aria-label="更新日"
          className="rounded border border-slate-200 bg-white px-2 py-1 text-xs focus:border-slate-400 focus:outline-none"
          value={urlState.updatedSince}
          onChange={(e) =>
            onPatch({
              updatedSince: e.target
                .value as InboxUrlState['updatedSince'],
            })
          }
        >
          {UPDATED_SINCE_PRESETS.map((p) => (
            <option key={p} value={p}>
              {UPDATED_SINCE_LABEL[p]}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

export interface OptionGroup {
  backlogProjectKey: string;
  items: ReadonlyArray<{ id: number; label: string }>;
}

/**
 * Group flat axis options by their Backlog project key while preserving
 * the server-side ordering inside each group. Multiple Backlog projects
 * share id namespaces only by accident — same numeric id from two
 * projects is two different things, so dedupe is (key, id) not just id.
 */
export function groupByProjectKey(
  options: ReadonlyArray<{ id: number; label: string; backlogProjectKey: string }>,
): OptionGroup[] {
  const byKey = new Map<string, { id: number; label: string }[]>();
  const order: string[] = [];
  const seen = new Set<string>();
  for (const o of options) {
    const dedupKey = `${o.backlogProjectKey} ${o.id}`;
    if (seen.has(dedupKey)) continue;
    seen.add(dedupKey);
    let bucket = byKey.get(o.backlogProjectKey);
    if (!bucket) {
      bucket = [];
      byKey.set(o.backlogProjectKey, bucket);
      order.push(o.backlogProjectKey);
    }
    bucket.push({ id: o.id, label: o.label });
  }
  return order.map((k) => ({ backlogProjectKey: k, items: byKey.get(k)! }));
}

/**
 * Flat (no grouping) multi-select over string-id options. Used for the
 * Backlog プロジェクト axis where every option is one Backlog project key
 * and there's nothing meaningful to subgroup by. Mirrors
 * GroupedMultiSelect's interaction model (panel-overlay, clear button,
 * pinned-state styling) so the filter bar feels uniform.
 */
export function StringMultiSelect({
  label,
  options,
  selected,
  onChange,
  loading,
}: {
  label: string;
  options: ReadonlyArray<{ id: string; label: string }>;
  selected: readonly string[];
  onChange: (ids: string[]) => void;
  loading: boolean;
}) {
  const [open, setOpen] = useState(false);
  const summary =
    selected.length === 0 ? label : `${label}: ${selected.length}`;
  const toggle = (id: string) => {
    const next = selected.includes(id)
      ? selected.filter((x) => x !== id)
      : [...selected, id];
    onChange(next);
  };
  return (
    <div className="relative">
      <button
        type="button"
        data-testid={`filter-${label}-button`}
        className={cn(
          'rounded border px-2 py-1 text-xs',
          selected.length > 0
            ? 'border-slate-900 bg-slate-900 text-white'
            : 'border-slate-200 bg-white text-slate-700 hover:border-slate-400',
        )}
        onClick={() => setOpen((o) => !o)}
        disabled={loading && options.length === 0}
      >
        {summary}
      </button>
      {open ? (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setOpen(false)}
            aria-hidden
          />
          <div className="absolute left-0 z-20 mt-1 max-h-72 w-56 overflow-auto rounded border border-slate-200 bg-white p-1 shadow-lg">
            {options.length === 0 ? (
              <div className="px-2 py-1 text-xs text-slate-500">
                {loading ? '読み込み中…' : '選択肢なし'}
              </div>
            ) : (
              options.map((o) => (
                <label
                  key={o.id}
                  className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-xs hover:bg-slate-50"
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(o.id)}
                    onChange={() => toggle(o.id)}
                  />
                  <span className="truncate">{o.label}</span>
                </label>
              ))
            )}
            {selected.length > 0 ? (
              <button
                type="button"
                className="mt-1 w-full rounded border border-slate-200 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50"
                onClick={() => onChange([])}
              >
                クリア
              </button>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  );
}

function GroupedMultiSelect({
  label,
  groups,
  selected,
  onChange,
  loading,
  projectNameByKey,
}: {
  label: string;
  groups: ReadonlyArray<OptionGroup>;
  selected: readonly number[];
  onChange: (ids: number[]) => void;
  loading: boolean;
  projectNameByKey?: ReadonlyMap<string, string>;
}) {
  const [open, setOpen] = useState(false);
  const total = groups.reduce((acc, g) => acc + g.items.length, 0);
  // Selected count is by id, ignoring which backlogProjectKey it came
  // from — the server-side filter axes are id arrays without per-key
  // scoping, matching Backlog's search API.
  const summary =
    selected.length === 0 ? label : `${label}: ${selected.length}`;
  return (
    <div className="relative">
      <button
        type="button"
        className={cn(
          'rounded border px-2 py-1 text-xs',
          selected.length > 0
            ? 'border-slate-900 bg-slate-900 text-white'
            : 'border-slate-200 bg-white text-slate-700 hover:border-slate-400',
        )}
        onClick={() => setOpen((o) => !o)}
        disabled={loading && total === 0}
      >
        {summary}
      </button>
      {open ? (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setOpen(false)}
            aria-hidden
          />
          <GroupedDropdownPanel
            groups={groups}
            selected={selected}
            onChange={onChange}
            loading={loading}
            projectNameByKey={projectNameByKey}
          />
        </>
      ) : null}
    </div>
  );
}

/**
 * Stateless panel body used by GroupedMultiSelect. Exported so render
 * tests can drive the panel directly without flipping the parent's
 * internal `open` state.
 */
export function GroupedDropdownPanel({
  groups,
  selected,
  onChange,
  loading,
  projectNameByKey,
}: {
  groups: ReadonlyArray<OptionGroup>;
  selected: readonly number[];
  onChange: (ids: number[]) => void;
  loading: boolean;
  projectNameByKey?: ReadonlyMap<string, string>;
}) {
  const total = groups.reduce((acc, g) => acc + g.items.length, 0);
  const toggle = (id: number) => {
    const next = selected.includes(id)
      ? selected.filter((x) => x !== id)
      : [...selected, id];
    onChange(next);
  };
  const showSectionHeaders = groups.length > 1;
  return (
    <div className="absolute left-0 z-20 mt-1 max-h-72 w-56 overflow-auto rounded border border-slate-200 bg-white p-1 shadow-lg">
      {total === 0 ? (
        <div className="px-2 py-1 text-xs text-slate-500">
          {loading ? '読み込み中…' : '選択肢なし'}
        </div>
      ) : (
        groups.map((g) => (
          <div key={g.backlogProjectKey} className="mb-1 last:mb-0">
            {showSectionHeaders ? (
              <div
                data-testid="filter-section-header"
                className="sticky top-0 z-[1] bg-slate-50 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500"
              >
                {formatBacklogProjectLabel(
                  g.backlogProjectKey,
                  projectNameByKey?.get(g.backlogProjectKey),
                )}
              </div>
            ) : null}
            {g.items.map((o) => (
              <label
                key={`${g.backlogProjectKey}-${o.id}`}
                className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-xs hover:bg-slate-50"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(o.id)}
                  onChange={() => toggle(o.id)}
                />
                <span className="truncate">{o.label}</span>
              </label>
            ))}
          </div>
        ))
      )}
      {selected.length > 0 ? (
        <button
          type="button"
          className="mt-1 w-full rounded border border-slate-200 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50"
          onClick={() => onChange([])}
        >
          クリア
        </button>
      ) : null}
    </div>
  );
}

function ServerFailureBanner({
  failure,
}: {
  failure: { reason: 'config-missing' | 'secret-error' | 'backlog-error'; message: string };
}) {
  return (
    <div className="flex items-start gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900 sm:px-6">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="flex-1">
        <div className="text-xs uppercase tracking-wide text-amber-700">
          {failure.reason}
        </div>
        <div>{failure.message}</div>
      </div>
    </div>
  );
}

function SuccessBanner({
  message,
  onClose,
}: {
  message: string;
  onClose: () => void;
}) {
  return (
    <div className="flex items-start gap-2 border-b border-emerald-200 bg-emerald-50 px-4 py-2 sm:px-6 text-sm text-emerald-800">
      <Check className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="flex-1">{message}</div>
      <button
        type="button"
        className="text-emerald-700 hover:underline"
        onClick={onClose}
        aria-label="閉じる"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

/**
 * One applied-filter chip. `label` is the axis ("状態", "Backlog", etc.);
 * `value` is the resolved display string (status name, project key,
 * keyword text…). Clicking the × calls `onRemove`.
 */
interface AppliedChip {
  /** Stable key for React reconciliation. Format: `axis:id`. */
  key: string;
  label: string;
  value: string;
  onRemove: () => void;
}

/**
 * Build the chip list from the current URL state + meta lookup. Kept
 * pure (no React) so the test layer can assert on it without rendering.
 *
 * Resolution rules:
 * - Status / assignee / category / milestone / priority chips use the
 *   meta callable's label; if the id isn't in `meta` yet (the cascade
 *   has narrowed the option list and removed the row), we fall back to
 *   "#<id>" so the user can still see and remove the orphan selection.
 * - Backlog project chips render `<key> <name>` when name is available.
 * - Keyword and updatedSince chips appear only when non-default.
 */
export function buildAppliedChips(
  urlState: InboxUrlState,
  meta: ReturnType<typeof useBacklogIssueMeta>['data'],
  projectNameByKey: ReadonlyMap<string, string>,
  onPatch: (patch: Partial<InboxUrlState>) => void,
): AppliedChip[] {
  const chips: AppliedChip[] = [];
  const statusName = (id: number) => {
    if (!meta?.ok) return `#${id}`;
    return meta.statuses.find((s) => s.id === id)?.name ?? `#${id}`;
  };
  const userName = (id: number) => {
    if (!meta?.ok) return `#${id}`;
    return meta.users.find((u) => u.id === id)?.name ?? `#${id}`;
  };
  const categoryName = (id: number) => {
    if (!meta?.ok) return `#${id}`;
    return meta.categories.find((c) => c.id === id)?.name ?? `#${id}`;
  };
  const milestoneName = (id: number) => {
    if (!meta?.ok) return `#${id}`;
    return meta.milestones.find((m) => m.id === id)?.name ?? `#${id}`;
  };
  const priorityName = (id: number) => {
    if (!meta?.ok) return `#${id}`;
    return meta.priorities.find((p) => p.id === id)?.name ?? `#${id}`;
  };

  for (const k of urlState.backlogProjectKeys) {
    chips.push({
      key: `bp:${k}`,
      label: 'Backlog',
      value: formatBacklogProjectLabel(k, projectNameByKey.get(k)),
      onRemove: () =>
        onPatch({
          backlogProjectKeys: urlState.backlogProjectKeys.filter((x) => x !== k),
        }),
    });
  }
  for (const id of urlState.statusIds) {
    chips.push({
      key: `st:${id}`,
      label: '状態',
      value: statusName(id),
      onRemove: () =>
        onPatch({ statusIds: urlState.statusIds.filter((x) => x !== id) }),
    });
  }
  for (const id of urlState.assigneeIds) {
    chips.push({
      key: `asg:${id}`,
      label: '担当',
      value: userName(id),
      onRemove: () =>
        onPatch({ assigneeIds: urlState.assigneeIds.filter((x) => x !== id) }),
    });
  }
  for (const id of urlState.categoryIds) {
    chips.push({
      key: `cat:${id}`,
      label: 'カテゴリ',
      value: categoryName(id),
      onRemove: () =>
        onPatch({ categoryIds: urlState.categoryIds.filter((x) => x !== id) }),
    });
  }
  for (const id of urlState.milestoneIds) {
    chips.push({
      key: `ms:${id}`,
      label: 'マイルストーン',
      value: milestoneName(id),
      onRemove: () =>
        onPatch({ milestoneIds: urlState.milestoneIds.filter((x) => x !== id) }),
    });
  }
  for (const id of urlState.priorityIds) {
    chips.push({
      key: `pr:${id}`,
      label: '優先度',
      value: priorityName(id),
      onRemove: () =>
        onPatch({ priorityIds: urlState.priorityIds.filter((x) => x !== id) }),
    });
  }
  if (urlState.keyword.trim().length > 0) {
    chips.push({
      key: 'q:keyword',
      label: 'キーワード',
      value: `"${urlState.keyword.trim()}"`,
      onRemove: () => onPatch({ keyword: '' }),
    });
  }
  if (urlState.updatedSince !== '90d') {
    chips.push({
      key: `us:${urlState.updatedSince}`,
      label: '更新',
      value: UPDATED_SINCE_LABEL[urlState.updatedSince],
      onRemove: () => onPatch({ updatedSince: '90d' }),
    });
  }
  return chips;
}

function AppliedFiltersRow({
  urlState,
  meta,
  projectNameByKey,
  onPatch,
}: {
  urlState: InboxUrlState;
  meta: ReturnType<typeof useBacklogIssueMeta>['data'];
  projectNameByKey: ReadonlyMap<string, string>;
  onPatch: (patch: Partial<InboxUrlState>) => void;
}) {
  const chips = buildAppliedChips(urlState, meta, projectNameByKey, onPatch);
  if (chips.length === 0) return null;
  const clearAll = () => {
    onPatch({
      statusIds: [],
      assigneeIds: [],
      categoryIds: [],
      milestoneIds: [],
      priorityIds: [],
      backlogProjectKeys: [],
      keyword: '',
      updatedSince: '90d',
    });
  };
  return (
    <div
      data-testid="applied-filters-row"
      className="flex flex-wrap items-center gap-1 border-b border-slate-200 bg-white px-4 py-1.5 text-xs sm:px-6"
    >
      <span className="mr-1 text-slate-500">適用中:</span>
      {chips.map((c) => (
        <span
          key={c.key}
          data-testid={`applied-chip-${c.key}`}
          className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-slate-50 px-2 py-0.5 text-slate-700"
        >
          <button
            type="button"
            aria-label={`${c.label}: ${c.value} を解除`}
            className="text-slate-500 hover:text-slate-900"
            onClick={c.onRemove}
          >
            <X className="h-3 w-3" />
          </button>
          <span className="text-slate-500">{c.label}:</span>
          <span className="font-medium">{c.value}</span>
        </span>
      ))}
      <button
        type="button"
        data-testid="applied-filters-clear-all"
        className="ml-auto rounded border border-slate-200 px-2 py-0.5 text-slate-600 hover:bg-slate-50"
        onClick={clearAll}
      >
        すべて解除
      </button>
    </div>
  );
}

function FilterTab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded px-3 py-1 text-sm transition-colors',
        active
          ? 'bg-slate-900 text-white'
          : 'text-slate-700 hover:bg-slate-100',
      )}
    >
      {children}
    </button>
  );
}

function EmptyState({
  filter,
  loading,
}: {
  filter: SuggestionFilter;
  loading: boolean;
}) {
  if (loading) {
    return (
      <div className="flex h-full items-center justify-center px-6 text-center text-sm text-slate-500">
        <Spinner />
      </div>
    );
  }
  const msg =
    filter === 'pending'
      ? '未処理のリンク候補はありません。'
      : filter === 'snoozed'
        ? 'あとでに回した候補はありません。'
        : filter === 'terminal'
          ? '終了した候補はまだありません。'
          : 'リンク候補はまだありません。';
  return (
    <div className="flex h-full items-center justify-center px-6 text-center text-sm text-slate-500">
      {msg}
    </div>
  );
}

function ErrorBanner({
  kind,
  message,
  onClose,
}: {
  kind: CallableErrorKind;
  message: string;
  onClose: () => void;
}) {
  const copy: Record<CallableErrorKind, string> = {
    rules: '権限ルールにより書き込みが拒否されました。プロジェクトの権限をご確認ください。',
    permission: 'この操作を行う権限がありません。',
    timeout: '処理がタイムアウトしました。しばらく待って再試行してください。',
    network: '通信エラーが発生しました。ネットワーク状態を確認してください。',
  };
  return (
    <div className="flex items-start gap-2 border-b border-red-200 bg-red-50 px-4 py-2 sm:px-6 text-sm text-red-800">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="flex-1">
        <div className="font-medium">{copy[kind]}</div>
        {message ? <div className="text-xs text-red-700/80">{message}</div> : null}
      </div>
      <button
        type="button"
        className="text-red-700 hover:underline"
        onClick={onClose}
        aria-label="閉じる"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

/**
 * Build the snapshot passed alongside `reactOnSuggestion` so the
 * callable can lazy-create the persisted doc when no row exists yet.
 * Mirrors `ReactOnSuggestionSnapshot` exactly.
 */
function buildSnapshot(item: BacklogSearchResultItem): ReactOnSuggestionSnapshot {
  return {
    issueKey: item.issueKey,
    backlogProjectKey: item.backlogProjectKey,
    issueSummary: item.issueSummary,
    issueUrl: item.issueUrl,
    issueStatus: item.issueStatus,
    assigneeId: item.assigneeId,
    assigneeName: item.assigneeName,
    issueTypeId: item.issueTypeId,
    issueTypeName: item.issueTypeName,
    categoryNames: item.categoryNames,
    milestoneNames: item.milestoneNames,
    priorityId: item.priorityId,
    priorityName: item.priorityName,
    dueDate: item.dueDate,
    issueCreatedAt: item.issueCreatedAt,
    kind: item.kind,
    candidates: item.candidates,
    candidateWbsItemIds: item.candidateWbsItemIds,
  };
}

function SuggestionCard({
  project,
  item,
  actionable,
  projectNameByKey,
  wbsItems,
  assignees,
  onError,
  onSuccess,
}: {
  project: ProjectDoc | null;
  item: BacklogSearchResultItem;
  actionable: boolean;
  projectNameByKey: ReadonlyMap<string, string>;
  wbsItems: readonly WbsItemDoc[];
  assignees: ReadonlyArray<{ assigneeId: string; name: string }>;
  onError: (e: { kind: CallableErrorKind; msg: string } | null) => void;
  onSuccess: (msg: string) => void;
}) {
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const [dismissOpen, setDismissOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const topCandidates: SuggestionCandidate[] = item.candidates.slice(0, 3);
  const eff = effectiveActionedState(item);
  const meta = item.actionedStateMeta;
  const snoozedByName =
    meta?.actorUserId && project
      ? project.members[meta.actorUserId]?.displayName ?? meta.actorUserId
      : null;
  const snoozeUntilStr = meta?.snoozedUntil
    ? formatDate(meta.snoozedUntil)
    : null;

  const handle = async (
    fn: () => Promise<unknown>,
    successMsg: string,
  ) => {
    setBusy(true);
    onError(null);
    try {
      await fn();
      onSuccess(successMsg);
    } catch (err) {
      const kind = classifyCallableError(err);
      const msg = err instanceof Error ? err.message : String(err);
      onError({ kind, msg });
    } finally {
      setBusy(false);
    }
  };

  const snapshot = buildSnapshot(item);

  const handleAccept = (wbsItemId: string) =>
    handle(
      () =>
        suggestionActions.acceptExisting({
          projectId: project?.projectId ?? '',
          suggestionId: item.issueKey,
          wbsItemId,
          snapshot,
        }),
      '紐付けました',
    );

  const handleDismiss = (reason: string) =>
    handle(async () => {
      await suggestionActions.dismiss({
        projectId: project?.projectId ?? '',
        suggestionId: item.issueKey,
        reason: reason.trim() || undefined,
        snapshot,
      });
      setDismissOpen(false);
    }, '却下しました');

  const handleSnooze = (hours: number, reason: string) =>
    handle(async () => {
      await suggestionActions.snooze({
        projectId: project?.projectId ?? '',
        suggestionId: item.issueKey,
        snoozeHours: hours,
        reason: reason.trim() || undefined,
        snapshot,
      });
      setSnoozeOpen(false);
    }, 'あとでに回しました');

  const handleRevive = () =>
    handle(
      () =>
        suggestionActions.revive({
          projectId: project?.projectId ?? '',
          suggestionId: item.issueKey,
        }),
      'pending に戻しました',
    );

  const handleCreateAndLink = (input: {
    name: string;
    phaseId: string;
    parentTaskId: string | null;
    assigneeId: string | null;
  }) =>
    handle(async () => {
      if (!project) throw new Error('project not loaded');
      // Parent is the task if one was picked, otherwise the phase itself.
      const parentItemId = input.parentTaskId ?? input.phaseId;
      const newItemId = await appendWbsItem(getDb(), project, wbsItems, {
        kind: 'task',
        parentItemId,
        name: input.name,
        assigneeId: input.assigneeId,
      });
      await suggestionActions.acceptNewWbsItem({
        projectId: project.projectId,
        suggestionId: item.issueKey,
        wbsItemId: newItemId,
        snapshot,
      });
      setCreateOpen(false);
    }, '作成して紐付けました');

  const canAcceptExisting =
    actionable && item.kind === 'link_to_wbs' && topCandidates.length > 0;

  return (
    <li className="card grid grid-cols-1 gap-4 px-4 py-3 md:grid-cols-2">
      <IssueColumn
        item={item}
        snoozedByName={snoozedByName}
        snoozeUntilStr={snoozeUntilStr}
        effectiveState={eff}
        backlogProjectName={projectNameByKey.get(item.backlogProjectKey) ?? null}
      />

      <div className="flex min-w-0 flex-col gap-3">
        {item.kind === 'create_new_wbs_item' ? (
          <div className="rounded border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-xs text-slate-600">
            マッチする WBS タスクが見つかりませんでした。次回リリースで「WBS に追加」できるようになります。
          </div>
        ) : topCandidates.length === 0 ? (
          <div className="rounded border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-xs text-slate-600">
            候補がまだありません。
          </div>
        ) : (
          <ul className="space-y-1.5">
            {topCandidates.map((c) => (
              <CandidateRow
                key={c.wbsItemId}
                candidate={c}
                disabled={!canAcceptExisting || busy}
                onAccept={() => handleAccept(c.wbsItemId)}
              />
            ))}
          </ul>
        )}

        <div className="mt-auto flex flex-wrap items-center justify-end gap-2">
          {actionable ? (
            <Button
              variant="secondary"
              disabled={busy}
              onClick={() => setCreateOpen(true)}
              data-testid="open-create-and-link"
            >
              <Plus className="mr-1 h-3.5 w-3.5" /> 新規WBSに追加
            </Button>
          ) : null}
          {actionable && eff === 'snoozed' ? (
            <Button variant="secondary" disabled={busy} onClick={handleRevive}>
              <RotateCcw className="mr-1 h-3.5 w-3.5" /> 戻す
            </Button>
          ) : null}
          {actionable && eff === 'pending' ? (
            <Button
              variant="secondary"
              disabled={busy}
              onClick={() => setSnoozeOpen(true)}
            >
              <Clock className="mr-1 h-3.5 w-3.5" /> あとで
            </Button>
          ) : null}
          {actionable ? (
            <Button
              variant="secondary"
              disabled={busy}
              onClick={() => setDismissOpen(true)}
            >
              <X className="mr-1 h-3.5 w-3.5" /> 却下
            </Button>
          ) : null}
        </div>
      </div>

      <CreateAndLinkDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        defaultName={item.issueSummary}
        wbsItems={wbsItems}
        assignees={assignees}
        busy={busy}
        onSubmit={handleCreateAndLink}
        project={project}
      />
      <SnoozeDialog
        open={snoozeOpen}
        onClose={() => setSnoozeOpen(false)}
        onSubmit={handleSnooze}
        busy={busy}
      />
      <DismissDialog
        open={dismissOpen}
        onClose={() => setDismissOpen(false)}
        onSubmit={handleDismiss}
        busy={busy}
      />
    </li>
  );
}

function formatDate(iso: string): string | null {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return null;
  const d = new Date(ms);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${mm}/${dd} ${hh}:${mi}`;
}

function IssueColumn({
  item,
  snoozedByName,
  snoozeUntilStr,
  effectiveState,
  backlogProjectName,
}: {
  item: BacklogSearchResultItem;
  snoozedByName: string | null;
  snoozeUntilStr: string | null;
  effectiveState: SuggestionStatus;
  backlogProjectName: string | null;
}) {
  return (
    <div className="min-w-0">
      <div className="flex flex-wrap items-start gap-2">
        <a
          href={item.issueUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 font-mono text-xs text-blue-700 hover:underline"
        >
          <ExternalLink className="h-3 w-3" />
          {item.issueKey}
        </a>
        <span
          data-testid="backlog-project-label"
          className="text-[11px] text-slate-500"
        >
          {formatBacklogProjectLabel(item.backlogProjectKey, backlogProjectName)}
        </span>
        <Badge tone="slate">{item.issueStatus}</Badge>
        <ActionedBadge state={effectiveState} original={item.actionedState} />
        {item.priorityName ? <Badge tone="yellow">{item.priorityName}</Badge> : null}
      </div>
      <div className="mt-1 truncate text-sm font-medium" title={item.issueSummary}>
        {item.issueSummary}
      </div>
      <div className="mt-0.5 text-xs text-slate-500">
        担当: {item.assigneeName ?? '(未割当)'}
        {item.categoryNames.length > 0 ? ` ・ ${item.categoryNames.join(', ')}` : ''}
        {item.milestoneNames.length > 0 ? ` ・ ${item.milestoneNames.join(', ')}` : ''}
      </div>
      {effectiveState === 'snoozed' && item.actionedState === 'snoozed' ? (
        <div className="mt-2 rounded bg-amber-50 px-2 py-1 text-xs text-amber-800">
          あとで by {snoozedByName ?? '?'}
          {snoozeUntilStr ? ` / 再表示 ${snoozeUntilStr}` : ''}
          {item.actionedStateMeta?.snoozeReason
            ? ` — ${item.actionedStateMeta.snoozeReason}`
            : ''}
        </div>
      ) : null}
    </div>
  );
}

function ActionedBadge({
  state,
  original,
}: {
  state: SuggestionStatus;
  original: SuggestionStatus;
}) {
  if (state === 'pending' && original === 'snoozed') {
    return <Badge tone="yellow">スヌーズ期限切れ</Badge>;
  }
  switch (state) {
    case 'pending':
      return <Badge tone="blue">未処理</Badge>;
    case 'snoozed':
      return <Badge tone="yellow">あとで</Badge>;
    case 'accepted':
      return <Badge tone="green">紐付け済み</Badge>;
    case 'dismissed':
      return <Badge tone="slate">却下</Badge>;
  }
}

function CandidateRow({
  candidate,
  disabled,
  onAccept,
}: {
  candidate: SuggestionCandidate;
  disabled: boolean;
  onAccept: () => void;
}) {
  const hasAncestry = Boolean(candidate.phaseName || candidate.parentTaskName);
  return (
    <li className="flex flex-col gap-1 rounded border border-slate-200 px-2 py-1.5">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium" title={candidate.wbsItemName}>
            {candidate.wbsItemName}
          </div>
        </div>
        <Badge tone="blue">{(candidate.score * 100).toFixed(0)}</Badge>
        <Button
          variant="secondary"
          className="!py-1 !text-xs"
          disabled={disabled}
          onClick={onAccept}
        >
          <Check className="mr-1 h-3 w-3" />
          紐付け
        </Button>
      </div>
      {hasAncestry ? (
        <div
          data-testid="candidate-ancestry"
          className="flex flex-wrap items-center gap-1 text-[10px]"
        >
          {candidate.phaseName ? (
            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-slate-600">
              フェーズ: {candidate.phaseName}
            </span>
          ) : null}
          {candidate.parentTaskName ? (
            <span className="inline-flex items-center gap-0.5 rounded bg-blue-50 px-1.5 py-0.5 text-blue-700">
              <CornerDownRight className="h-2.5 w-2.5" />
              親: {candidate.parentTaskName}
            </span>
          ) : null}
        </div>
      ) : null}
      {candidate.reasons.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {candidate.reasons.map((r, i) => (
            <span
              key={`${candidate.wbsItemId}-${i}`}
              className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-600"
            >
              {r}
            </span>
          ))}
        </div>
      ) : null}
    </li>
  );
}

function SnoozeDialog({
  open,
  onClose,
  onSubmit,
  busy,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (hours: number, reason: string) => void;
  busy: boolean;
}) {
  const [hours, setHours] = useState(SNOOZE_OPTIONS[1]!.hours);
  const [reason, setReason] = useState('');
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="あとで"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            キャンセル
          </Button>
          <Button disabled={busy} onClick={() => onSubmit(hours, reason)}>
            登録
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <Label>再表示まで</Label>
          <div className="mt-1 flex flex-wrap gap-1">
            {SNOOZE_OPTIONS.map((o) => (
              <button
                key={o.hours}
                type="button"
                className={cn(
                  'rounded-full border px-3 py-1 text-xs',
                  hours === o.hours
                    ? 'border-slate-900 bg-slate-900 text-white'
                    : 'border-slate-200 bg-white text-slate-700 hover:border-slate-400',
                )}
                onClick={() => setHours(o.hours)}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <Label>メモ (任意)</Label>
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="例: 後続フェーズ後に再判断"
          />
        </div>
      </div>
    </Dialog>
  );
}

function DismissDialog({
  open,
  onClose,
  onSubmit,
  busy,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (reason: string) => void;
  busy: boolean;
}) {
  const [reason, setReason] = useState('');
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="却下"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            キャンセル
          </Button>
          <Button variant="danger" disabled={busy} onClick={() => onSubmit(reason)}>
            却下
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-xs text-slate-500">
          却下した候補はヒストリに残り、将来の学習用データになります。
        </p>
        <div>
          <Label>理由 (任意)</Label>
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="例: 管理対象外の issue"
          />
        </div>
      </div>
    </Dialog>
  );
}
